import React, { useCallback, useContext, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import * as Yup from "yup";
import * as styles from "./FixCouponErrorForm.scss";
import includes from "lodash/includes";
import debounce from "lodash/debounce";
import map from "lodash/map";
import { checkValidStateZip } from "api/CouponsApi";
import { windowSizeContext } from "AppRoot";
import Button from "components/common/Button";
import CheckboxInput from "components/common/CheckboxInput";
import DateInput from "components/common/DateInput";
import TextBox from "components/common/TextBox";
import logger from "utils/logger";
import toast from "utils/toast";
import useFormHandlers from "utils/useFormHandlers";
import Dropdown from "components/common/Dropdown";
import {patientWeightUnitsDropdownList} from "constants/PatientWeightUnits";

// Constants for validation field names
const VALIDATION_FIELD_NAMES = {
    PATIENT_WEIGHT: 'Patient Weight',
    PATIENT_WEIGHT_UNIT: 'Patient Weight Unit'
};

// Constants
const DEBOUNCE_DELAY = 500;
const DEFAULT_PET_WEIGHT_UNIT = 'pounds';
const MIN_PET_WEIGHT = 0.01;

const createSchema = (includePetWeight) => {
    const baseSchema = {
        firstName: Yup.string().required(`First Name is required`),
        lastName: Yup.string().required(`Last Name is required`),
        emailRequired: Yup.bool(),
        email: Yup.string()
            .emailWithDomain()
            .when("emailRequired", (emailRequired, schema) => {
                return emailRequired[0] ? schema.required() : schema.nullable(true);
            }),
        address1: Yup.string().required(`Address 1 is required`),
        address2: Yup.string().nullable(true),
        city: Yup.string().required(`City is required`),
        state: Yup.string().min(2).max(2).required(`State is required`),
        postalCode: Yup.string().required(`Postal Code is required`),
        petName: Yup.string().required(`Pet Name is required`),
        petDOB: Yup.date().required(),
    };

    if (includePetWeight) {
        baseSchema.petWeight = Yup.number()
            .nullable()
            .transform((value, originalValue) => {
                if (originalValue === "") return null;
                if (typeof originalValue === "string") {
                    const parsed = parseFloat(originalValue);
                    return isNaN(parsed) ? null : parsed;
                }
                return value;
            })
            .test('required', 'Pet Weight is required', function(value) {
                return value !== null && value !== undefined;
            })
            .test('minimum', `Pet Weight minimum is ${MIN_PET_WEIGHT}`, function(value) {
                // Only check minimum if value exists
                return value === null || value === undefined || value >= MIN_PET_WEIGHT;
            });
        baseSchema.petWeightUnit = Yup.string().required();
    }

    return Yup.object().shape(baseSchema);
};

export default function FixCouponErrorForm(props) {
    const { submitLabel = "Submit" } = props;
    
    // Determine if pet weight fields should be included BEFORE initializing the hook
    const includePetWeight = !!(props.initialData?.fields?.find(field => field.validationFieldName === VALIDATION_FIELD_NAMES.PATIENT_WEIGHT) 
        || props.initialData?.fields?.find(field => field.validationFieldName === VALIDATION_FIELD_NAMES.PATIENT_WEIGHT_UNIT));

    // Preprocess initial data to replace zero petWeight with null
    const preparedInitialData = useMemo(() => {
        if (!props.initialData) return props.initialData;
        
        const processedData = { ...props.initialData };
        if (includePetWeight && (processedData.petWeight === 0 || processedData.petWeight === "0")) {
            processedData.petWeight = null;
        }
        return processedData;
    }, [props.initialData, includePetWeight]);

    // Create the appropriate schema based on whether pet weight is included
    const formSchema = createSchema(includePetWeight);
    const {formData, isValid, handleUpdate, invalidFields, changedData, validationErrors} = useFormHandlers(
        preparedInitialData,
        formSchema,
        props.initialData?.errors || [],
    );
    const [addressCityStatePostalCodeValid, setAddressCityStatePostalCodeValid] = useState(true);
    const [skipEmail, setSkipEmail] = useState(false);
    const emailError = includes(formData.errors, "email");
    const context = useContext(windowSizeContext) || {};
    const maxDate = new moment(formData?.invoiceDate).format("MMM DD YYYY");
    const minDate = "Jan 01 1900";
    const checkAddressValid = useCallback(debounce((city, state, postalCode) => {
        checkValidStateZip({ city, state, postalCode })
            .then((res) => {
                setAddressCityStatePostalCodeValid(res?.body?.valid);
            })
            .catch((err) => {
                logger.error("VALIDATION ERROR: ", err, true);
                setAddressCityStatePostalCodeValid(false);
            });
    }, DEBOUNCE_DELAY), []);

    useEffect(() => {
        checkAddressValid(formData.city, formData.state, formData.postalCode);
    }, [formData, checkAddressValid]);

    useEffect(() => {
        // Set default pet weight unit to "pounds" if it's empty and pet weight fields are included
        if (includePetWeight && (!formData.petWeightUnit || formData.petWeightUnit === "")) {
            handleUpdate({ name: "petWeightUnit", value: DEFAULT_PET_WEIGHT_UNIT });
        }
    }, [includePetWeight, formData.petWeightUnit, handleUpdate]);

    const handleSkipEmail = ({ value }) => {
        setSkipEmail(value);
        handleUpdate({ name: "email", value: "" });
    };

    //TODO morph this functionalityinto a generic component

    const renderFieldError = (fieldName) => {
        const fieldValue = formData[fieldName];
        const isEmpty = fieldValue === null || fieldValue === undefined || fieldValue === "" || fieldValue === 0;
        const errorMessage = validationErrors[fieldName];
        
        if (errorMessage) {
            // If field is empty and has validation error, likely a required field error
            if (isEmpty) {
                return (
                    <div className={styles.fieldError}>
                        {errorMessage}
                    </div>
                );
            }
            // If field has value and has validation error, show the error (like minimum validation)
            else {
                return (
                    <div className={styles.fieldError}>
                        {errorMessage}
                    </div>
                );
            }
        }
        return null;
    };

    const handleSubmit = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if(formData.petDOB?.value){
            const petDOB = new moment(formData.petDOB.value);
            if(petDOB.isBefore(minDate)) {
                toast.error("Pet Date of Birth is before 1900");
                return;
            } else if(petDOB.isAfter(maxDate)){
                toast.error("Pet Date of Birth is after the invoice date");
                return;
            }
        }

        const requiredFields = {};
        map(formData.errors, errorField => {
            requiredFields[errorField] = formData[errorField];
        });
        const dataToSubmit = {
            ...requiredFields,
            ...changedData,
        };

        props.onSubmit(formData, dataToSubmit);
    };

    const renderSubmitButton = () => {
        if (!props.onSubmit || (emailError && skipEmail)) {
            return null;
        }
        const formValid = isValid && addressCityStatePostalCodeValid;
        return (
            <Button
                data-testid="fix_coupon_error_form_submit_btn"
                className="margin-left-auto margin-top-sm"
                disabled={!formValid}
                large
                buttonType="submit"
            >
                {submitLabel}
            </Button>
        );
    };
    return (
        <form
            data-testid="fix_coupon_error_form_component"
            className={styles.couponErrorForm}
            onSubmit={handleSubmit}
        >
            <div className={styles.couponDetails}>
                <div className="flex-none">
                    <div className={styles.detailItem}>
                        <label>Invoice Date:</label>
                        <span>{new moment(formData?.invoiceDate).format("MM/DD/YYYY")}</span>
                    </div>
                    <div className={styles.detailItem}>
                        <label>Invoice Number:</label>
                        <span>{formData?.invoiceNumber}</span>
                    </div>
                </div>
                <div className="flex-none">
                    <div className={styles.detailItem}>
                        <label>Offer &amp; Code:</label>
                        <span>{formData?.offerCode} <small>(19-12345)</small></span>
                    </div>
                    <div className={styles.detailItem}>
                        <label>Products:</label>
                        <span>{formData?.product}</span>
                    </div>
                </div>
            </div>
            <div className={styles.editableFields}>
                {Object.keys(validationErrors).length > 0 && (
                    <div className={styles.errorSummary}>
                        <strong>Please fix the following errors:</strong>
                        <ul className={styles.errorList}>
                            {Object.entries(validationErrors).map(([field, message]) => (
                                <li key={field} className={styles.errorListItem}>{message}</li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className={styles.field}>
                    <TextBox
                        data-testid="fix_coupon_error_form_first_name"
                        onChange={handleUpdate}
                        name="firstName"
                        hasError={includes(invalidFields, "firstName")}
                        value={formData.firstName}
                        label="First Name"
                        alignedLeft={!context.isPhone}
                        required
                    />
                    <TextBox
                        data-testid="fix_coupon_error_form_last_name"
                        onChange={handleUpdate}
                        name="lastName"
                        hasError={includes(invalidFields, "lastName")}
                        value={formData.lastName}
                        label="Last Name"
                        alignedLeft={!context.isPhone}
                        required
                    />
                    <TextBox
                        data-testid="fix_coupon_error_form_email"
                        onChange={handleUpdate}
                        name="email"
                        hasError={includes(invalidFields, "email")}
                        value={formData.email}
                        disabled={skipEmail}
                        label="Client Email"
                        alignedLeft={!context.isPhone}
                        required={formData.emailRequired}
                    />
                    {emailError && (
                        <>
                            <CheckboxInput
                                data-testid="fix_coupon_error_form_skip_email"
                                name="skipEmail"
                                onChange={handleSkipEmail}
                                label={<span>No Client Email</span>}
                                checked={skipEmail}
                                extraStyles={{
                                    paddingLeft: "125px",
                                    paddingBottom: "0.25em"
                                }}
                            />
                            <div
                                style={{
                                    paddingLeft: "125px",
                                    paddingBottom: "1em"
                                }}
                                className="text-sm"
                            >
                                ***Please note by selecting "Decline" on this coupon for not having a valid email address you will not be missing out on any funds or reimbursement to your clinic.
                                Offers requiring a valid email address are purely client participation based and do not affect clinics funds if an email is not provided.
                                <b> Please note adding your clinic email address to this field is fraudulent.</b>
                            </div>
                        </>
                    )}
                    <TextBox
                        data-testid="fix_coupon_error_form_address_1"
                        onChange={handleUpdate}
                        name="address1"
                        hasError={includes(invalidFields, "address1")}
                        value={formData.address1}
                        label="Address 1"
                        alignedLeft={!context.isPhone}
                        required
                    />
                    <TextBox
                        data-testid="fix_coupon_error_form_address_2"
                        onChange={handleUpdate}
                        name="address2"
                        hasError={includes(invalidFields, "address2")}
                        value={formData.address2}
                        label="Address 2"
                        alignedLeft={!context.isPhone}
                    />
                    <TextBox
                        data-testid="fix_coupon_error_form_city"
                        onChange={handleUpdate}
                        name="city"
                        hasError={!addressCityStatePostalCodeValid || includes(invalidFields, "city")}
                        value={formData.city}
                        label="City"
                        alignedLeft={!context.isPhone}
                        required
                    />
                    <TextBox
                        data-testid="fix_coupon_error_form_state"
                        onChange={handleUpdate}
                        name="state"
                        hasError={!addressCityStatePostalCodeValid || includes(invalidFields, "state")}
                        value={formData.state}
                        label="State"
                        alignedLeft={!context.isPhone}
                        maxLength={2}
                        required
                    />
                    <TextBox
                        data-testid="fix_coupon_error_form_postal_code"
                        onChange={handleUpdate}
                        name="postalCode"
                        hasError={!addressCityStatePostalCodeValid || includes(invalidFields, "postalCode")}
                        value={formData.postalCode}
                        label="Postal Code"
                        alignedLeft={!context.isPhone}
                        required
                    />
                    <TextBox
                        data-testid="fix_coupon_error_form_pet_name"
                        onChange={handleUpdate}
                        name="petName"
                        hasError={includes(invalidFields, "petName")}
                        value={formData.petName}
                        label="Pet Name"
                        alignedLeft={!context.isPhone}
                        required
                    />
                    <DateInput
                        data-testid="fix_coupon_error_form_pet_dob"
                        onChange={handleUpdate}
                        name="petDOB"
                        hasError={includes(invalidFields, "petDOB")}
                        value={formData.petDOB}
                        label="Pet Date Of Birth"
                        aligned={!context.isPhone}
                        minDate={minDate}
                        maxDate={maxDate}
                        showDropdowns
                        required
                    />
                    {(includePetWeight) && (
                        <>
                            <div className="flex spaced-content">
                                <div className="flex-1">
                                    <TextBox
                                        data-testid="fix_coupon_error_form_pet_weight"
                                        onChange={(update) => {
                                            // Allow up to 7 digits before decimal, optional decimal and up to 2 digits after
                                            const val = update.value;
                                            if (val === "" || /^\d{0,7}(\.\d{0,2})?$/.test(val)) {
                                                handleUpdate({
                                                    name: update.name,
                                                    value: val === "" ? null : val
                                                });
                                            }
                                            // else: ignore the input (do not update state)
                                        }}
                                        name="petWeight"
                                        hasError={includes(invalidFields, "petWeight")}
                                        value={formData.petWeight === null || formData.petWeight === undefined ? "" : formData.petWeight}                                        
                                        label="Pet Weight"
                                        alignedLeft={!context.isPhone}
                                        type="number"
                                        inputMode="decimal"
                                        information="Maximum of 7 digits before decimal, optional decimal and up to 2 digits after. Minimum value is .01"
                                        step="any"
                                        required
                                    />
                                </div>
                                <div className="flex-1">
                                    <Dropdown
                                        onChange={handleUpdate}
                                        name="petWeightUnit"
                                        value={formData.petWeightUnit}
                                        label="Weight Unit"
                                        options={patientWeightUnitsDropdownList}
                                        required
                                        hasError={includes(invalidFields, "petWeightUnit")}
                                        alignedLeft={!context.isPhone}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className="margin-top-md">*These changes to the client name or address are only temporary. Permanent changes must be done in your Practice Management System.</div>
            <div className={styles.bottomButtons}>
                {!!props.showDecline && (
                    <Button
                        data-testid="fix_coupon_error_form_decline_btn"
                        className="margin-left-auto margin-top-sm"
                        onClick={props.onDecline}
                        type="danger"
                        large
                    >
                        Decline
                    </Button>
                )}
                <Button
                    data-testid="fix_coupon_error_form_cancel_btn"
                    className="margin-left-auto margin-top-sm"
                    onClick={props.onClose}
                    type={"gray"}
                    large
                >
                    Cancel
                </Button>
                {renderSubmitButton()}
            </div>
        </form>
    );
}

FixCouponErrorForm.propTypes = {
    initialData: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func,
    onDecline: PropTypes.func,
    submitLabel: PropTypes.string,
    showDecline: PropTypes.bool,
};
